// src/components/strumenti/CoordinamentoInterruttoreCavo.js
// Verifica CEI 64-8: Ib ≤ In ≤ Iz (corrente di impiego ≤ corrente nominale interruttore ≤
// portata del cavo). Include anche il promemoria sulla verifica di intervento If ≤ 1,45 × Iz.
// La portata (Iz) può essere scritta a mano oppure suggerita da una tabella di riferimento
// (CEI-UNEL 35024/1 / IEC 364-5-523, più metodi di posa, PVC o XLPE, rame, 30°C) — resta
// sempre un punto di partenza modificabile, mai un valore imposto: raggruppamento con altri
// circuiti e altre condizioni reali vanno sempre verificate per il caso specifico.
import React, { useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import { METODI_POSA, SEZIONI_DISPONIBILI, K1_PVC, K1_XLPE, TEMPERATURE_DISPONIBILI } from "../../utils/portataCaviUtils";
import { leggiNumero } from "../../utils/numeriStrumenti";

const inp = { padding:"10px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };
const TAGLIE_IN = [6,10,13,16,20,25,32,40,50,63,80,100,125,160,200,250,315,400];

export default function CoordinamentoInterruttoreCavo() {
  const [ib, setIb] = useState("");
  const [inTaglia, setInTaglia] = useState("");
  const [iz, setIz] = useState("");
  const [metodo, setMetodo] = useState("b");
  const [isolante, setIsolante] = useState("pvc");
  const [sezioneRif, setSezioneRif] = useState("");
  const [nConduttori, setNConduttori] = useState(3);
  const [temperatura, setTemperatura] = useState(30);

  const Ib = leggiNumero(ib)||0;
  const In = leggiNumero(inTaglia)||0;
  const Iz = leggiNumero(iz)||0;

  const tutteInserite = Ib>0 && In>0 && Iz>0;
  const cond1 = Ib<=In;
  const cond2 = In<=Iz;
  const conforme = tutteInserite && cond1 && cond2;

  const K1 = isolante==="pvc" ? K1_PVC : K1_XLPE;
  const baseSelezionata = sezioneRif ? METODI_POSA[metodo][isolante][sezioneRif]?.[nConduttori] : null;

  const suggerisciIz = () => {
    if (baseSelezionata) setIz((baseSelezionata * (K1[temperatura] ?? 1)).toFixed(1));
  };

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
        <div>
          <label style={lbl}>Corrente di impiego Ib (A)</label>
          <input value={ib} onChange={e=>setIb(e.target.value)} style={inp} placeholder="Es. 28"/>
        </div>
        <div>
          <label style={lbl}>Corrente nominale interruttore In (A)</label>
          <select value={inTaglia} onChange={e=>setInTaglia(e.target.value)} style={inp}>
            <option value="">—</option>
            {TAGLIE_IN.map(v => <option key={v} value={v}>{v} A</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Portata cavo Iz (A)</label>
          <input value={iz} onChange={e=>setIz(e.target.value)} style={inp} placeholder="Es. 41"/>
        </div>
      </div>

      <div style={{ background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:8, padding:"10px 12px", marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#6d28d9", marginBottom:8, display:"flex", alignItems:"center", gap:5 }}>
          <Sparkles size={12}/> Suggerisci Iz da tabella di riferimento (CEI-UNEL 35024/1)
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
          <div>
            <label style={{...lbl, fontSize:10}}>Metodo di posa</label>
            <select value={metodo} onChange={e=>setMetodo(e.target.value)} style={{...inp, padding:"7px 10px", fontSize:12.5}}>
              {Object.entries(METODI_POSA).map(([id,m]) => <option key={id} value={id}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{...lbl, fontSize:10}}>Isolante</label>
            <select value={isolante} onChange={e=>setIsolante(e.target.value)} style={{...inp, padding:"7px 10px", fontSize:12.5}}>
              <option value="pvc">PVC</option>
              <option value="xlpe">XLPE / EPR</option>
            </select>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr auto", gap:8, alignItems:"end" }}>
          <div>
            <label style={{...lbl, fontSize:10}}>Sezione (mm²)</label>
            <select value={sezioneRif} onChange={e=>setSezioneRif(e.target.value)} style={{...inp, padding:"7px 10px", fontSize:12.5}}>
              <option value="">—</option>
              {SEZIONI_DISPONIBILI.filter(s => METODI_POSA[metodo][isolante][s]?.[nConduttori]).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={{...lbl, fontSize:10}}>Conduttori caricati</label>
            <select value={nConduttori} onChange={e=>setNConduttori(Number(e.target.value))} style={{...inp, padding:"7px 10px", fontSize:12.5}}>
              <option value={2}>2 (es. monofase)</option>
              <option value={3}>3 (es. trifase)</option>
            </select>
          </div>
          <div>
            <label style={{...lbl, fontSize:10}}>Temperatura ambiente</label>
            <select value={temperatura} onChange={e=>setTemperatura(Number(e.target.value))} style={{...inp, padding:"7px 10px", fontSize:12.5}}>
              {TEMPERATURE_DISPONIBILI.filter(t => K1[t]!==undefined).map(t => <option key={t} value={t}>{t}°C{t===30?" (rif.)":""}</option>)}
            </select>
          </div>
          <button onClick={suggerisciIz} disabled={!baseSelezionata}
            style={{ padding:"7px 14px", borderRadius:7, border:"none", background: baseSelezionata?"#7c3aed":"#d1d5db", color:"#fff", fontSize:12, fontWeight:700, cursor: baseSelezionata?"pointer":"not-allowed", whiteSpace:"nowrap" }}>
            Usa questo valore
          </button>
        </div>
        {sezioneRif && !baseSelezionata && (
          <div style={{ fontSize:10.5, color:"#dc2626", marginTop:6 }}>Nessun dato tabellare per questa combinazione (sezione/metodo/conduttori) — la norma non copre questa sezione per questo metodo di posa.</div>
        )}
        <div style={{ fontSize:10, color:"#7c5fb8", marginTop:8 }}>
          Valore corretto per la temperatura scelta (fattore K1). Cavi raggruppati con altri circuiti riducono ulteriormente la portata reale (fattore K2, qui non applicato) — verifica sempre per il caso specifico. Resta comunque un campo modificabile a mano.
        </div>
      </div>

      {tutteInserite && (
        <div style={{ background: conforme ? "#f0fdf4" : "#fef2f2", border:`1px solid ${conforme?"#bbf7d0":"#fca5a5"}`, borderRadius:10, padding:"14px 16px" }}>
          <div style={{ textAlign:"center", fontSize:16, fontWeight:800, color: conforme ? "#15803d" : "#dc2626", marginBottom:10 }}>
            {conforme ? "✅ Coordinamento verificato" : "⚠️ Coordinamento NON verificato"}
          </div>
          <div style={{ textAlign:"center", fontSize:15, fontFamily:"monospace", marginBottom:8 }}>
            <span style={{color: cond1?"#15803d":"#dc2626", fontWeight:700}}>Ib {Ib}</span>
            <span style={{color:"#6b7280"}}> ≤ </span>
            <span style={{color: cond1&&cond2?"#15803d":"#dc2626", fontWeight:700}}>In {In}</span>
            <span style={{color:"#6b7280"}}> ≤ </span>
            <span style={{color: cond2?"#15803d":"#dc2626", fontWeight:700}}>Iz {Iz}</span>
          </div>
          {!cond1 && <div style={{ fontSize:11, color:"#991b1b", textAlign:"center" }}>La corrente di impiego supera la taglia dell'interruttore — aumentare In</div>}
          {cond1 && !cond2 && <div style={{ fontSize:11, color:"#991b1b", textAlign:"center" }}>L'interruttore protegge il cavo in modo insufficiente — aumentare la sezione o ridurre In</div>}
        </div>
      )}
      <div style={{ fontSize:10, color:"#9ca3af", marginTop:8, textAlign:"center" }}>
        Verifica CEI 64-8 di base (Ib ≤ In ≤ Iz). Ricorda anche la verifica di intervento: If ≤ 1,45 × Iz, generalmente già soddisfatta dagli interruttori magnetotermici modulari con questa selezione.
      </div>

      <button onClick={()=>{setIb("");setInTaglia("");setIz("");setSezioneRif("");setNConduttori(3);setTemperatura(30);setMetodo("b");setIsolante("pvc");}}
        style={{ marginTop:12, display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
        <RefreshCw size={12}/> Azzera
      </button>
    </div>
  );
}
